import { NotificationBell as BellView } from "@loot-os/design-system/domain/notifications/NotificationBell";
import {
  type NotificationPanelView,
  NotificationPanel as PanelView,
} from "@loot-os/design-system/domain/notifications/NotificationPanel";
import { formatUnreadBadge } from "@loot-os/design-system/domain/notifications/notificationFormat";
import { useEffect, useState } from "react";

import {
  bellRangThisSession,
  markBellRang,
  type NotificationFilter,
  notificationsEnabled,
  useMarkNotificationsRead,
  useUnreadNotifications,
} from "../../lib/notifications.js";
import { useShopper } from "../../lib/shopper.js";
import { useTenant } from "../../lib/tenant.js";
import {
  useNotificationListContent,
  useNotificationSettingsContent,
} from "./notificationContent.js";

/** The header's bell, for a signed-in shopper on a store with notifications on. */
export function NotificationBell() {
  const { user } = useShopper();
  const { config } = useTenant();
  if (!user || !notificationsEnabled(config)) return null;
  return <Bell userId={user.id} />;
}

/**
 * The design system's bell and its unread badge, opening the panel in a popover, or a
 * full-screen sheet on a phone. Mounted once, it also keeps the count live and toasts what
 * arrives.
 */
function Bell({ userId }: { userId: string }) {
  const unreadCount = useUnreadNotifications();
  const [open, setOpen] = useState(false);
  // It rings for unread notifications it finds on load once a browser session, then only when one
  // arrives, so a shopper who leaves them unread isn't rung at on every visit.
  const [ringOnLoad] = useState(() => !bellRangThisSession(userId));
  const hasUnread = unreadCount !== undefined && unreadCount > 0;
  useEffect(() => {
    if (hasUnread) markBellRang(userId);
  }, [hasUnread, userId]);
  const badge = formatUnreadBadge(unreadCount ?? 0);
  return (
    <BellView
      // The name says what the badge shows ("9+" past nine), so the two never disagree (WCAG 2.5.3).
      label={badge ? `Notifications, ${badge} unread` : "Notifications"}
      badge={badge}
      unreadCount={unreadCount}
      ringOnLoad={ringOnLoad}
      panelLabel="Notifications"
      open={open}
      onOpenChange={setOpen}
    >
      {({ phone }) => (
        <Panel unreadCount={unreadCount ?? 0} onClose={() => setOpen(false)} showClose={phone} />
      )}
    </BellView>
  );
}

/**
 * What the bell opens: the notifications under All / Unread tabs, or the shopper's settings
 * behind the gear. The list is fetched when the panel opens; between, only the badge is kept.
 */
function Panel({
  unreadCount,
  onClose,
  showClose,
}: {
  unreadCount: number;
  onClose: () => void;
  showClose: boolean;
}) {
  const [view, setView] = useState<NotificationPanelView>("list");
  const [tab, setTab] = useState<NotificationFilter>("all");
  const markRead = useMarkNotificationsRead();
  const unreadBadge = formatUnreadBadge(unreadCount);
  const list = useNotificationListContent(tab, {
    onRead: (id) => markRead.mutate({ ids: [id] }),
    onNavigate: onClose,
  });
  const settings = useNotificationSettingsContent(view === "settings");

  return (
    <PanelView
      title="Notifications"
      view={view}
      onViewChange={setView}
      tabs={{
        value: tab,
        label: "Show notifications",
        options: [
          { value: "all", label: "All" },
          { value: "unread", label: unreadBadge ? `Unread · ${unreadBadge}` : "Unread" },
        ],
        onValueChange: setTab,
      }}
      list={list}
      markAllRead={{
        label: "Mark all as read",
        disabled: unreadCount === 0,
        onClick: () => markRead.mutate({ all: true }),
      }}
      settings={{
        ...settings,
        title: "Notification settings",
        openLabel: "Notification settings",
        backLabel: "Back to notifications",
      }}
      close={showClose ? { label: "Close notifications", onClose } : undefined}
    />
  );
}
